import {useEffect, useRef, useState} from "react";
import {connect, useDispatch} from "react-redux";
import {Navigate, useNavigate} from "react-router-dom";

import UpImage from "../../../images/image-upload.svg";
import {useAddFile, useDeleteFile} from "../../../hooks/useFile";
import {useMe} from "../../../hooks/useGetUser";
import {verifAction} from "../../../store/actions/AuthActions";
import {
  getUser,
  isAuthenticated,
  isLoading as selectIsLoading,
} from "../../../store/selectors/AuthSelectors";
import {
  accountStatus,
  documentType,
  fileTypeRelation,
} from "../../../utils/constants";
import {
  DOCUMENT_ACCEPT_ATTRIBUTE,
  getUploadErrorMessage,
  isPdfDocument,
  validateDocumentFile,
} from "../../../utils/documentUpload";
import {buildImageUrl} from "../../../utils/imageUrlBuilder";
import {fetchPrivateDocumentUrl} from "../../../services/privateDocumentService";
import {identityTypesToClear} from "../../../utils/identityDocuments";
import DocumentPreview from "../shared/DocumentPreview";
import Loader from "../shared/Loader";
import {showToastModal} from "../shared/ToastModalSwal";

const EMPTY_IDENTITY = {
  [documentType.CNI_UP]: null,
  [documentType.CNI_DOWN]: null,
  [documentType.PASSPORT]: null,
};

const LABELS = {
  [documentType.CNI_UP]: "CNI recto",
  [documentType.CNI_DOWN]: "CNI verso",
  [documentType.PASSPORT]: "Passeport",
};

const ProfileDiplomes = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const diplomaInputRef = useRef();
  const {data, isLoading, isError} = useMe();
  const {onAdd} = useAddFile();
  const {onDelete} = useDeleteFile();

  const [selectedDocument, setSelectedDocument] = useState("cni");
  const [identity, setIdentity] = useState(EMPTY_IDENTITY);
  const [diplomas, setDiplomas] = useState([]);
  const [uploads, setUploads] = useState({});
  const [deletes, setDeletes] = useState([]);
  const [identityChanged, setIdentityChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    const storedFiles = data?.Technician?.FileUploads;
    if (!storedFiles) return;
    let active = true;
    const privateUrls = [];

    const loadDocuments = async () => {
      const nextIdentity = {...EMPTY_IDENTITY};
      const nextDiplomas = [];
      for (const file of storedFiles) {
        const isIdentity = Object.hasOwn(nextIdentity, file.type);
        let filePath = buildImageUrl(file.path);
        if (isIdentity && file.id) {
          try {
            filePath = await fetchPrivateDocumentUrl(file.id);
            if (!active) {
              URL.revokeObjectURL(filePath);
              return;
            }
            privateUrls.push(filePath);
          } catch (_) {
            filePath = "";
          }
        }
        const document = {
          ...file,
          filePath,
          isPdf: isPdfDocument(file),
          persisted: true,
        };
        if (file.type === documentType.DIPLOMA) nextDiplomas.push(document);
        else if (isIdentity) nextIdentity[file.type] = document;
      }
      if (!active) return;
      setIdentity(nextIdentity);
      setDiplomas(nextDiplomas);
      if (nextIdentity[documentType.PASSPORT] && !nextIdentity[documentType.CNI_UP]) {
        setSelectedDocument("passport");
      }
    };
    loadDocuments();
    return () => {
      active = false;
      privateUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [data]);

  if (props.isLoading || isLoading) return <Loader />;
  if (!props.isAuthenticated || isError) return <Navigate to="/login" replace />;

  const technicianId = data?.Technician?.id || props.user?.Technician?.id;
  const identityLocked = data?.Technician?.status === accountStatus.VALID;

  const buildUpload = (file, type) => {
    const validationError = validateDocumentFile(file);
    if (validationError) {
      showToastModal({
        success: false,
        title: "Fichier non accepté",
        message: validationError,
      });
      return null;
    }

    const formData = new FormData();
    formData.append("fileUpload", file);
    formData.append("type", type);
    formData.append("fileUploadId", technicianId);
    formData.append("fileUploadType", fileTypeRelation.technician);
    return {
      formData,
      document: {
        filePath: URL.createObjectURL(file),
        name: file.name,
        isPdf: isPdfDocument(file),
        persisted: false,
      },
    };
  };

  const chooseIdentity = (file, type) => {
    if (identityLocked) return;
    if (!file) return;
    const pending = buildUpload(file, type);
    if (!pending) return;
    const previous = identity[type];
    if (previous?.persisted && previous.id) {
      setDeletes((current) => [...new Set([...current, previous.id])]);
    }
    setIdentity((current) => ({...current, [type]: pending.document}));
    setUploads((current) => ({...current, [type]: pending.formData}));
    setIdentityChanged(true);
  };

  const chooseDiploma = (file) => {
    if (!file) return;
    const pending = buildUpload(file, documentType.DIPLOMA);
    if (!pending) return;
    const localId = pending.document.filePath;
    setDiplomas((current) => [...current, {...pending.document, localId}]);
    setUploads((current) => ({...current, [localId]: pending.formData}));
  };

  const removeIdentity = (type) => {
    if (identityLocked) return;
    const document = identity[type];
    if (document?.persisted && document.id) {
      setDeletes((current) => [...new Set([...current, document.id])]);
    }
    setUploads((current) => {
      const next = {...current};
      delete next[type];
      return next;
    });
    setIdentity((current) => ({...current, [type]: null}));
    setIdentityChanged(true);
  };

  const changeIdentityType = (selection) => {
    if (identityLocked || selection === selectedDocument) return;
    const obsoleteTypes = identityTypesToClear(selection, documentType);
    const obsoleteIds = obsoleteTypes
      .map((type) => identity[type])
      .filter((document) => document?.persisted && document.id)
      .map((document) => document.id);

    if (obsoleteIds.length) {
      setDeletes((current) => [...new Set([...current, ...obsoleteIds])]);
    }
    setUploads((current) => {
      const next = {...current};
      obsoleteTypes.forEach((type) => delete next[type]);
      return next;
    });
    setIdentity((current) => {
      const next = {...current};
      obsoleteTypes.forEach((type) => {
        next[type] = null;
      });
      return next;
    });
    setSelectedDocument(selection);
    setIdentityChanged(true);
  };

  const removeDiploma = (document) => {
    if (document.persisted && document.id) {
      setDeletes((current) => [...new Set([...current, document.id])]);
    }
    if (document.localId) {
      setUploads((current) => {
        const next = {...current};
        delete next[document.localId];
        return next;
      });
    }
    setDiplomas((current) => current.filter((item) => item !== document));
  };

  const save = async () => {
    const hasIdentity =
      selectedDocument === "passport"
        ? Boolean(identity[documentType.PASSPORT])
        : Boolean(identity[documentType.CNI_UP] && identity[documentType.CNI_DOWN]);
    if (!hasIdentity) {
      showToastModal({
        success: false,
        title: "Pièce d’identité incomplète",
        message:
          selectedDocument === "passport"
            ? "Ajoutez votre passeport."
            : "Ajoutez le recto et le verso de votre carte d’identité.",
      });
      return;
    }

    setSaving(true);
    try {
      await Promise.all([
        ...Object.entries(uploads).map(([key, formData]) =>
          onAdd(formData, ({loaded, total}) =>
            setUploadProgress((current) => ({
              ...current,
              [key]: total ? Math.round((loaded / total) * 100) : 0,
            })),
          ),
        ),
        ...deletes.map((id) => onDelete({id})),
      ]);

      if (identityChanged) {
        await dispatch(
          verifAction(
            {
              id: technicianId,
              body: {status: accountStatus.VALIDATION_WAITING},
            },
            navigate,
          ),
        );
      }

      await showToastModal({
        success: true,
        title: "Documents enregistrés",
        message: identityChanged
          ? "Vos documents ont été enregistrés et seront vérifiés par notre équipe."
          : "Vos diplômes et certifications ont été enregistrés.",
        duration: 3000,
      });
      setUploads({});
      setDeletes([]);
      setUploadProgress({});
    } catch (error) {
      showToastModal({
        success: false,
        title: "Importation impossible",
        message: getUploadErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const visibleTypes =
    selectedDocument === "passport"
      ? [documentType.PASSPORT]
      : [documentType.CNI_UP, documentType.CNI_DOWN];

  return (
    <div className="flex w-full flex-col gap-8">
      <section className="flex flex-col gap-4 p-4">
        <div>
          <h2 className="text-xl font-semibold text-[#19295C]">Pièce d’identité</h2>
          <p className="mt-1 text-sm text-[#667085]">
            {identityLocked
              ? "Votre pièce d’identité a été validée par le backoffice. Elle est disponible en lecture seule."
              : "Formats acceptés : JPG, PNG, WEBP et PDF. 1 fichier par emplacement, 10 Mo maximum. Cliquez sur un document pour le visualiser."}
          </p>
        </div>
        <div className="flex gap-5">
          <label className={`flex items-center gap-2 ${identityLocked ? "cursor-default opacity-70" : "cursor-pointer"}`}>
            <input
              type="radio"
              checked={selectedDocument === "cni"}
              disabled={identityLocked}
              onChange={() => changeIdentityType("cni")}
            />
            Carte d’identité
          </label>
          <label className={`flex items-center gap-2 ${identityLocked ? "cursor-default opacity-70" : "cursor-pointer"}`}>
            <input
              type="radio"
              checked={selectedDocument === "passport"}
              disabled={identityLocked}
              onChange={() => changeIdentityType("passport")}
            />
            Passeport
          </label>
        </div>
        <div className="flex flex-wrap gap-5">
          {visibleTypes.map((type) =>
            identity[type] ? (
              <DocumentPreview
                key={type}
                document={identity[type]}
                label={LABELS[type]}
                onDelete={identityLocked ? undefined : () => removeIdentity(type)}
                uploading={saving && Boolean(uploads[type])}
                progress={uploadProgress[type] || 0}
              />
            ) : identityLocked ? (
              <div
                key={type}
                className="flex h-[160px] w-[200px] items-center justify-center rounded-xl border border-[#D6E2EF] bg-[#F8FAFC] px-4 text-center text-sm text-[#667085]"
              >
                Document validé non disponible
              </div>
            ) : (
              <label
                key={type}
                className="relative flex h-[160px] w-[200px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1A64B9]"
              >
                <input
                  type="file"
                  accept={DOCUMENT_ACCEPT_ATTRIBUTE}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  onChange={(event) => {
                    chooseIdentity(event.target.files?.[0], type);
                    event.target.value = "";
                  }}
                />
                <img src={UpImage} alt="" className="h-14 w-14" />
                <span className="mt-2 font-medium text-[#1A64B9]">{LABELS[type]}</span>
                <span className="text-[11px] text-[#667085]">Image ou PDF · 10 Mo max</span>
              </label>
            ),
          )}
        </div>
      </section>

      <section className="flex flex-col gap-4 border-t border-[#EAECF0] p-4 pt-7">
        <div>
          <h2 className="text-xl font-semibold text-[#19295C]">
            Diplômes et certifications <span className="font-normal text-[#667085]">(facultatif)</span>
          </h2>
          <p className="mt-1 text-sm text-[#667085]">
            Ajoutez 1 fichier à la fois, 10 Mo maximum par fichier.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <button
            type="button"
            onClick={() => diplomaInputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              chooseDiploma(event.dataTransfer.files?.[0]);
            }}
            className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1A64B9] bg-white px-4"
          >
            <img src={UpImage} alt="" className="h-14 w-14" />
            <span className="mt-2 font-semibold text-[#19295C]">
              Glissez un fichier ici ou cliquez pour parcourir
            </span>
            <span className="mt-1 text-xs text-[#667085]">Image ou PDF · 10 Mo max</span>
          </button>
          <input
            ref={diplomaInputRef}
            type="file"
            accept={DOCUMENT_ACCEPT_ATTRIBUTE}
            className="hidden"
            onChange={(event) => {
              chooseDiploma(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {diplomas.map((document) => (
              <DocumentPreview
                key={document.id || document.localId || document.filePath}
                document={document}
                label={document.name || "Diplôme ou certification"}
                onDelete={() => removeDiploma(document)}
                className="!h-auto !w-full aspect-square"
                uploading={saving && Boolean(uploads[document.localId])}
                progress={uploadProgress[document.localId] || 0}
              />
            ))}
          </div>
        </div>
      </section>

      <div className="flex justify-end p-4">
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="btn flex w-[250px] items-center justify-center rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white disabled:opacity-60"
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
  isLoading: selectIsLoading(state),
  user: getUser(state),
});

export default connect(mapStateToProps)(ProfileDiplomes);
